import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import Header from "../../components/ui/Header";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";

import WorkshopCrudTable from "./components/WorkshopCrudTable";
import WorkshopFormModal from "./components/WorkshopFormModal";
import WorkshopViewModal from "./components/WorkshopViewModal";
import DeleteConfirmModal from "../users-crud-management/components/DeleteConfirmModal";

import {
  getWorkshopsService,
  createWorkshopService,
  updateWorkshopService,
  deleteWorkshopService,
  getTagsService,
  getWorkshopByIdService,
} from "../../redux/services/workshops_service";
import { getAllgovernoratesService } from "redux/services/general_service";

import { toast } from "react-toastify";

const WorkshopsCrudManagement = () => {
  const dispatch = useDispatch();
  const { workshopsList, metaWorkshops, workshopInfo } = useSelector(
    (state) => state.workshops,
  );

  const [language, setLanguage] = useState("ar");
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);

  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const [selectedWorkshop, setSelectedWorkshop] = useState(null);
  const [formMode, setFormMode] = useState("add");

  useEffect(() => {
    const savedLanguage = localStorage.getItem("language") || "ar";
    setLanguage(savedLanguage);

    dispatch(getWorkshopsService({ page, search: searchQuery, emergency: 0 }));
    dispatch(getAllgovernoratesService());
    dispatch(getTagsService());
  }, [dispatch, page, searchQuery]);

  const labels = {
    title: { ar: "إدارة الورش", en: "Workshops Management" },
    add: { ar: "إضافة ورشة", en: "Add Workshop" },
    search: { ar: "البحث عن ورشة...", en: "Search workshops..." },
  };
  const handleExport = () => {
    console.log("Export users");
    const url = `https://sayartak.devoracompany.com/api/v1/export/workshops`;

    window.open(url, "_blank");
  };
  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="container mx-auto px-4 py-6">
        {/* Title */}
        <div className="mb-8">
          <h1 className="text-3xl font-semibold">{labels.title[language]}</h1>
        </div>

        {/* Search & Actions */}
        <div className="bg-card rounded-xl p-6 shadow border border-border mb-8">
          <div className="flex flex-col lg:flex-row gap-4 justify-between">
            <Input
              placeholder={labels.search[language]}
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                dispatch(
                  getWorkshopsService({ search: e.target.value, emergency: 0 }),
                );
                setPage(1);
              }}
              className="lg:max-w-md"
            />
            <div className="flex gap-2">
              <Button
                variant="outline"
                iconName="Download"
                onClick={handleExport}
              >
                تصدير
              </Button>

              <Button
                variant="default"
                iconName="Plus"
                onClick={() => {
                  setFormMode("add");
                  setSelectedWorkshop(null);
                  setIsFormModalOpen(true);
                }}
              >
                {labels.add[language]}
              </Button>
            </div>
          </div>
        </div>

        {/* Table */}
        <WorkshopCrudTable
          workshops={workshopsList}
          metaWorkshops={metaWorkshops}
          language={language}
          onPageChange={(p) => setPage(p)}
          onView={(w) => {
            setSelectedWorkshop(w);
            dispatch(getWorkshopByIdService(w?.id));
            setIsViewModalOpen(true);
          }}
          onEdit={(w) => {
            setSelectedWorkshop(w);
            setFormMode("edit");
            setIsFormModalOpen(true);
          }}
          onDelete={(w) => {
            setSelectedWorkshop(w);
            setIsDeleteModalOpen(true);
          }}
        />

        {/* Form Modal */}
        {isFormModalOpen && (
          <WorkshopFormModal
            isOpen
            workshop={selectedWorkshop}
            mode={formMode}
            language={language}
            onClose={() => setIsFormModalOpen(false)}
            onSave={(data) => {
              if (formMode === "add") {
                dispatch(createWorkshopService(data)).then((response) => {
                  if (
                    response?.payload?.status === 200 ||
                    response?.payload?.status === 201
                  ) {
                    toast.success("تمت عملية الإضافة بنجاح!");
                    dispatch(getWorkshopsService({ page, emergency: 0 }));
                    setIsFormModalOpen(false);
                  } else {
                    Object.values(response.payload.response?.data?.errors)
                      .flatMap((errorArray) => errorArray)
                      .forEach((error) => toast.warn(error));
                  }
                });
              } else {
                dispatch(
                  updateWorkshopService({
                    id: selectedWorkshop.id,
                    formData: data,
                  }),
                ).then((response) => {
                  if (
                    response?.payload?.status === 200 ||
                    response?.payload?.status === 201
                  ) {
                    toast.success("تم التحديث بنجاح!");
                    dispatch(getWorkshopsService({ page, emergency: 0 }));
                    setIsFormModalOpen(false);
                  } else {
                    Object.values(response.payload.response?.data?.errors)
                      .flatMap((errorArray) => errorArray)
                      .forEach((error) => toast.warn(error));
                  }
                });
              }
            }}
          />
        )}

        {/* View Modal */}
        {isViewModalOpen && (
          <WorkshopViewModal
            isOpen
            workshop={selectedWorkshop}
            language={language}
            onClose={() => setIsViewModalOpen(false)}
            onEdit={() => {
              setIsViewModalOpen(false);
              setFormMode("edit");
              setIsFormModalOpen(true);
            }}
            onDelete={() => {
              setIsViewModalOpen(false);
              setIsDeleteModalOpen(true);
            }}
          />
        )}

        {/* Delete Modal */}
        {isDeleteModalOpen && (
          <DeleteConfirmModal
            isOpen
            itemName={selectedWorkshop?.name}
            language={language}
            onClose={() => setIsDeleteModalOpen(false)}
            onConfirm={() => {
              dispatch(deleteWorkshopService(selectedWorkshop.id)).then(() => {
                toast("تم الحذف بنجاح");
                setIsDeleteModalOpen(false);
                dispatch(getWorkshopsService({ page, emergency: 0 }));
              });
            }}
          />
        )}
      </main>
    </div>
  );
};

export default WorkshopsCrudManagement;
